import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ImageOff, LayoutGrid, LayoutTemplate, List, Loader2, Search } from 'lucide-react';
import { useCreativeStrategyStore } from '@/stores/creativeStrategy';
import { useClientStore } from '@/stores/client';
import {
  creativeStrategyApi,
  type StrategyDocketSummary,
  type StrategyTemplateSearch,
  type StrategyTemplateThumb,
} from '@/services/api';

/**
 * The mined templates a client's docket serves, searched the way the agents search
 * them (search_creative_templates): by meaning when a query is given, with the
 * inputs each template still needs when a brief is given.
 */
export function StrategyTemplatesPage() {
  const { packs, loading: packsLoading, fetchPacks } = useCreativeStrategyStore();
  const selectedClientUid = useClientStore((s) => s.selectedClientUid);
  const [libraries, setLibraries] = useState<StrategyDocketSummary[]>([]);
  const [clientUid, setClientUid] = useState('');
  const [query, setQuery] = useState('');
  const [briefId, setBriefId] = useState('');
  const [fillableOnly, setFillableOnly] = useState(false);
  const [result, setResult] = useState<StrategyTemplateSearch | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'gallery' | 'table'>('gallery');
  const [media, setMedia] = useState<Record<string, StrategyTemplateThumb[]>>({});

  useEffect(() => {
    fetchPacks();
    creativeStrategyApi.listDockets().then((r) => setLibraries(r.dockets)).catch(() => undefined);
  }, [fetchPacks]);

  const serving = useMemo(() => packs.filter((p) => p.enabled).map((p) => p.client_uid), [packs]);

  // Default to the client chosen in the header when it has a docket, else the first
  // client that draws on a library (only those have exemplar images to show).
  useEffect(() => {
    if (clientUid || serving.length === 0) return;
    if (selectedClientUid && serving.includes(selectedClientUid)) {
      setClientUid(selectedClientUid);
      return;
    }
    if (libraries.length === 0) return; // wait for the libraries before choosing
    const withLibrary = serving.find((c) => libraries.some((l) => l.member_clients.includes(c)));
    setClientUid(withLibrary ?? serving[0]);
  }, [clientUid, serving, selectedClientUid, libraries]);

  const library = libraries.find((l) => l.member_clients.includes(clientUid));

  // Thumbnails of the ads each template was mined from (admin only; the agents' search carries none).
  useEffect(() => {
    setMedia({});
    if (!library) return;
    creativeStrategyApi
      .templateMedia(library.docket_id)
      .then((r) => setMedia(r.templates))
      .catch(() => setMedia({}));
  }, [library?.docket_id]);

  const run = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!clientUid) return;
    setLoading(true);
    setError(null);
    try {
      setResult(
        await creativeStrategyApi.searchTemplates({
          clientUid,
          query: query.trim(),
          briefId: briefId.trim() || null,
          k: 60,
          fillableOnly: fillableOnly && !!briefId.trim(),
        }),
      );
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Browse everything for the client as soon as one is chosen.
  useEffect(() => {
    if (clientUid) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUid]);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-navy-900 flex items-center justify-center text-white">
            <LayoutTemplate size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-navy-900">Templates</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Ad structures mined into each library, searched the way the agents search them: by what the ad should do,
              and, given a brief, by which inputs each template still needs.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <form onSubmit={run} className="mb-4 flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-500">
            Client docket
            <select
              value={clientUid}
              onChange={(e) => setClientUid(e.target.value)}
              className="mt-1 block rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-navy-900"
            >
              {serving.length === 0 && (
                <option value="">{packsLoading ? 'Loading clients…' : 'No client is served category evidence'}</option>
              )}
              {serving.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-500 flex-1 min-w-[16rem]">
            What should the ad do?
            <div className="mt-1 flex items-center rounded-lg border border-slate-300 bg-white px-2">
              <Search size={14} className="text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. a framed print hung in a real living room"
                className="w-full px-2 py-1.5 text-sm text-navy-900 outline-none"
              />
            </div>
          </label>
          <label className="text-xs text-slate-500">
            Brief (optional)
            <input
              value={briefId}
              onChange={(e) => setBriefId(e.target.value)}
              placeholder="brief_…"
              className="mt-1 block w-44 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-navy-900"
            />
          </label>
          <label className="flex items-center gap-1.5 pb-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={fillableOnly}
              disabled={!briefId.trim()}
              onChange={(e) => setFillableOnly(e.target.checked)}
            />
            Only what the brief can fill
          </label>
          <div className="flex rounded-lg border border-slate-300 bg-white p-0.5">
            {(['gallery', 'table'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                title={v === 'gallery' ? 'Gallery' : 'Table'}
                className={clsx('rounded-md p-1.5', view === v ? 'bg-navy-900 text-white' : 'text-slate-500')}
              >
                {v === 'gallery' ? <LayoutGrid size={14} /> : <List size={14} />}
              </button>
            ))}
          </div>
          <button
            type="submit"
            disabled={!clientUid || loading}
            className="rounded-lg bg-navy-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            Search
          </button>
        </form>

        {result && (
          <p className="mb-3 text-xs text-slate-500">
            {result.templates.length} of {result.total_templates} templates served to {result.client_uid}
            {library && (
              <>
                {' '}from the{' '}
                <Link
                  to={`/knowledge/creative-strategy/library/${encodeURIComponent(library.docket_id)}`}
                  className="text-navy-700 underline"
                >
                  {library.name}
                </Link>{' '}
                library
              </>
            )}
            {result.pack_version && <span className="font-mono"> · {result.pack_version}</span>}
            {result.query ? ' · ordered by match to your words' : ' · ordered by evidence, strongest first'}
          </p>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Searching templates…
          </div>
        ) : result && result.templates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
            No template matches. Clear "Only what the brief can fill" or change the words.
          </div>
        ) : result && view === 'gallery' ? (
          <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(15rem,1fr))]">
            {result.templates.map((t) => {
              const thumbs = media[t.template_id] ?? [];
              return (
                <div
                  key={t.template_id}
                  className={clsx(
                    'rounded-xl border bg-white overflow-hidden flex flex-col',
                    t.current ? 'border-brand-teal' : 'border-slate-200',
                  )}
                >
                  {thumbs.length > 0 ? (
                    <div className="bg-slate-100">
                      {/* The best exemplar: a still that ran longest. */}
                      <img
                        src={thumbs[0].thumb_url}
                        alt={thumbs[0].advertiser ? `Ad by ${thumbs[0].advertiser}` : 'Exemplar ad'}
                        loading="lazy"
                        className="w-full aspect-square object-cover"
                      />
                      <div className="flex items-center justify-between gap-2 px-2 py-1 text-[10px] text-slate-500 bg-white border-b border-slate-100">
                        <span className="truncate">{thumbs[0].advertiser || 'Advertiser unknown'}</span>
                        {thumbs[0].days_active != null && <span>ran {Math.round(thumbs[0].days_active)} days</span>}
                      </div>
                      {thumbs.length > 1 && (
                        <div className="grid grid-cols-3 gap-px">
                          {thumbs.slice(1, 4).map((m) => (
                            <img
                              key={m.ad_key}
                              src={m.thumb_url}
                              alt={m.advertiser ? `Ad by ${m.advertiser}` : 'Exemplar ad'}
                              title={m.advertiser}
                              loading="lazy"
                              className="w-full aspect-square object-cover"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-square bg-slate-50 flex flex-col items-center justify-center gap-2 p-4 text-center text-xs text-slate-400">
                      <ImageOff size={20} />
                      {library ? 'No exemplar image indexed' : 'This client draws on no library, so no exemplar images'}
                    </div>
                  )}
                  <div className="p-3 flex-1 flex flex-col gap-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm text-navy-900 leading-snug">{t.name}</div>
                      {result.query && (
                        <span className="text-[11px] tabular-nums text-slate-400" title="Match to your words">
                          {t.match.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {t.ads} ads · {t.advertisers} advertisers
                    </div>
                    {t.best_for && <div className="text-xs text-slate-500 line-clamp-2">{t.best_for}</div>}
                    {t.fillable !== undefined && (
                      <div className={clsx('mt-auto pt-1 text-xs', t.fillable ? 'text-emerald-700' : 'text-amber-700')}>
                        {t.fillable
                          ? 'Brief fills every input'
                          : `Missing: ${(t.unbound_slots ?? []).join(', ') || 'a recreation prompt'}`}
                      </div>
                    )}
                    {t.current && <div className="text-[11px] text-brand-teal">On this angle</div>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : result ? (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Template</th>
                  <th className="text-left px-4 py-2.5 font-medium">Structure</th>
                  {result.query && <th className="text-right px-4 py-2.5 font-medium">Match</th>}
                  <th className="text-right px-4 py-2.5 font-medium">Ads</th>
                  <th className="text-right px-4 py-2.5 font-medium">Advertisers</th>
                  <th className="text-left px-4 py-2.5 font-medium">Inputs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.templates.map((t) => (
                  <tr key={t.template_id} className={clsx('align-top', t.current && 'bg-teal-50/50')}>
                    <td className="px-4 py-2.5 max-w-xs">
                      <div className="font-medium text-navy-900">
                        {t.name}
                        {t.current && <span className="ml-2 text-[10px] text-brand-teal">on this angle</span>}
                      </div>
                      <div className="text-xs text-slate-400 font-mono">{t.template_id}</div>
                      {t.best_for && <div className="mt-1 text-xs text-slate-500">Best for: {t.best_for}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-md">
                      {t.structure || '—'}
                      {t.evidence && <div className="mt-1 text-xs text-slate-400 italic">{t.evidence}</div>}
                    </td>
                    {result.query && <td className="px-4 py-2.5 text-right tabular-nums">{t.match.toFixed(2)}</td>}
                    <td className="px-4 py-2.5 text-right tabular-nums">{t.ads}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{t.advertisers}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {t.fillable === undefined ? (
                        <span className="text-slate-500">{t.slots.join(', ') || '—'}</span>
                      ) : t.fillable ? (
                        <span className="text-emerald-700">Brief fills every input</span>
                      ) : (
                        <span className="text-amber-700">
                          Missing: {(t.unbound_slots ?? []).join(', ') || 'a recreation prompt'}
                        </span>
                      )}
                      {!t.has_recreation_prompt && (
                        <div className="text-slate-400">No recreation prompt, so it cannot reach a provider</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
